import { Box, Button, Flex, Text, useBreakpointValue, useDisclosure, useToast } from "@chakra-ui/react";
import {
  useDefaultSupplierQuestionnaireConfig,
  useFinalizeSupplierQuestionnaire,
  useGetCountries,
  useGetScopeTwoConsumptionTypes
} from "../../../../../../hooks/query";
import * as React from "react";
import { Fragment, useEffect, useState } from "react";
import { ConsumptionsGrid, ConsumptionsModal } from "../ConsumptionsGrid";
import {
  consumptionBasedScopeTwoKeys,
  useAddConsumptionBasedIndirectEmissionsItem,
  useDeleteConsumptionBasedIndirectEmissionsItem,
  useGetConsumptionBasedIndirectEmissionsItems,
  useUpdateConsumptionBasedIndirectEmissionsItem
} from "../../../../../../hooks/query/supplier-questionnaire/tabs";
import { showErrorToast, showSuccessToast } from "../../../../../../utils";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { t } from "i18next";
import { ConfirmationDialog } from "../../../../../../components/ConfirmationDialog";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import { defaultScopeTwoGridColumns, mapScopeTwoFormDataToApiData, SCOPES } from "../../helpers";

function ConsumptionBasedScopeTwo({
                                    isTabActive,
                                    defaultValues,
                                    handleTabChange,
                                    isPreview,
                                    returnToListRequested,
                                    returnToList
                                  }) {
  const { id: questionnaireId } = defaultValues;

  const queryClient = useQueryClient();
  const toast = useToast();
  const dispatch = useDispatch();

  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const {
    isOpen: isConsumptionsModalOpen,
    onOpen: onConsumptionsModalOpen,
    onClose: onConsumptionsModalClose
  } = useDisclosure();
  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const buttonsWidth = useBreakpointValue({
    base: "24",
    md: "32"
  });

  const ELECTRICITY_SECTION = "electricity";

  const [selectedItem, setSelectedItem] = useState(null);
  const [consumptionsModalData, setConsumptionsModalData] = useState(null);
  const [changeTabDirection, setChangeTabDirection] = useState("");
  const [sectionsSize, setSectionsSize] = useState({});

  const {
    data: consumptionTypes
  } = useGetScopeTwoConsumptionTypes({
    onError: err => err.response?.status !== 404 ? showErrorToast(toast, err.message) : null,
    onSettled: () => dispatch(hideAppSpinner())
  });

  const {
    data: countries
  } = useGetCountries({
    onError: err => err.response?.status !== 404 ? showErrorToast(toast, err.message) : null,
    onSettled: () => dispatch(hideAppSpinner())
  });

  useEffect(() => {
    if (changeTabDirection !== "") {
      if (changeTabDirection === "previous") {
        handleTabChange(prev => prev - 1);
        setChangeTabDirection("");
      } else if (changeTabDirection === "next") {
        handleTabChange(prev => prev + 1);
        setChangeTabDirection("");
      }
    }
  }, [changeTabDirection, handleTabChange]);

  useEffect(() => {
    if (isTabActive === true && returnToListRequested === true) {
      returnToList();
    }
  }, [isTabActive, returnToListRequested, returnToList]);

  const { mutate: finalize } = useFinalizeSupplierQuestionnaire({
    ...useDefaultSupplierQuestionnaireConfig(),
    onSuccess: () => {
      showSuccessToast(toast, t("supplierQuestionnaire.page.finalized"));
      returnToList();
    }
  });

  const { mutate: addItem } = useAddConsumptionBasedIndirectEmissionsItem({
    onSuccess: () => {
      handleConsumptionsModalClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(consumptionBasedScopeTwoKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateConsumptionBasedIndirectEmissionsItem({
    onSuccess: () => {
      handleConsumptionsModalClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(consumptionBasedScopeTwoKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: deleteItem } = useDeleteConsumptionBasedIndirectEmissionsItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(consumptionBasedScopeTwoKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleFinalizeConfirmation = () => {
    finalize(questionnaireId);
    onConfirmDialogClose();
  };

  const handleDeleteItemClick = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id, type: ELECTRICITY_SECTION });
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id, type: selectedItem.type });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const handleEditItemClick = (item) => {
    setConsumptionsModalData({
      consumptionTypes,
      scope: SCOPES.SCOPE_TWO,
      fieldArrayName: ELECTRICITY_SECTION,
      consumptionTypesAttr: "electricityTypes",
      editData: item,
      countries: countries,
      columnNames: defaultScopeTwoGridColumns
    });
    onConsumptionsModalOpen();
  };

  const handleAddItemClick = () => {
    setConsumptionsModalData({
      consumptionTypes,
      scope: SCOPES.SCOPE_TWO,
      fieldArrayName: ELECTRICITY_SECTION,
      consumptionTypesAttr: "electricityTypes",
      countries: countries,
      columnNames: [...defaultScopeTwoGridColumns, ""]
    });
    onConsumptionsModalOpen();
  };

  const handleConsumptionsModalSubmit = (data) => {
    const apiData = mapScopeTwoFormDataToApiData(data[consumptionsModalData.fieldArrayName]);
    dispatch(showAppSpinner());
    if (consumptionsModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0],
        type: consumptionsModalData.fieldArrayName
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData,
        type: consumptionsModalData.fieldArrayName
      });
    }
  };

  const handleConsumptionsModalClose = () => {
    setConsumptionsModalData(null);
    onConsumptionsModalClose();
  };

  const handleSectionSizeChange = (section, size) => {
    setSectionsSize({
      ...sectionsSize,
      [section]: size
    });
  };

  return (
    <Fragment>
      {isPreview && sectionsSize && Object.keys(sectionsSize).length === 1 &&
      typeof Object.keys(sectionsSize).find(key => sectionsSize[key] > 0) === "undefined" ?
        <Text fontSize="lg" fontWeight="bold">
          {t("supplierQuestionnaire.consumptionBasedRevenue.noDataAvailable")}
        </Text> :
        <Box overflow="auto">
          {questionnaireId && consumptionTypes && countries && (
            <ConsumptionsGrid
              questionnaireId={questionnaireId}
              consumptionTypes={consumptionTypes}
              scope={SCOPES.SCOPE_TWO}
              sectionType={ELECTRICITY_SECTION}
              consumptionTypesAttr={"electricity"}
              countries={countries}
              getItemsFunction={useGetConsumptionBasedIndirectEmissionsItems}
              handleAddItem={() => handleAddItemClick()}
              handleEditItem={(dataItem) => handleEditItemClick(dataItem)}
              handleDeleteItem={(dataItem) => handleDeleteItemClick(dataItem)}
              isPreview={isPreview}
              onDataItemsChange={handleSectionSizeChange}
            />
          )}
        </Box>
      }
      {!isPreview &&
        <Flex
          direction="row"
          justify="space-between"
          gap={4}
          mt={8}
          position="sticky"
          bottom="0"
          bg="white"
          borderTop="1px solid"
          borderColor="gray.200"
          py={4}
          zIndex="10"
        >
          <Flex gap="2">
            <Button
              bg="limeShade.100"
              color="pureBlack"
              _hover={{ bg: "limeShade.200" }}
              width="32"
              type="submit"
              onClick={() => {
                setChangeTabDirection("previous");
              }}
            >
              {t("buttons.previous")}
            </Button>
            <Button
              bg="limeShade.100"
              color="pureBlack"
              _hover={{ bg: "limeShade.200" }}
              width="32"
              type="submit"
              onClick={() => {
                setChangeTabDirection("next");
              }}
            >
              {t("buttons.next")}
            </Button>
          </Flex>
          <Button
            bg="lime"
            color="pureBlack"
            _hover={{ bg: "limeShade.300" }}
            width={buttonsWidth}
            onClick={() => {
              onConfirmDialogOpen();
            }}
          >
            {t("buttons.finalize")}
          </Button>
        </Flex>
      }
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={onConfirmDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={t("questionnaireFinalizationDialog.header")}
        confirmationText={t("questionnaireFinalizationDialog.question")}
      />
      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {
        isConsumptionsModalOpen && consumptionsModalData !== null && countries !== null && (
          <ConsumptionsModal
            modalData={consumptionsModalData}
            isOpen={isConsumptionsModalOpen}
            onClose={handleConsumptionsModalClose}
            onSubmit={handleConsumptionsModalSubmit}
            scope={SCOPES.SCOPE_TWO}
          />
        )
      }
    </Fragment>
  );
}

export default ConsumptionBasedScopeTwo;